/**
 * User-journey reference data, generated from the '[Master] User Journey by
 * Vertical' Google Sheet (via extract_journey_data.py) and ported verbatim from
 * the real journey_data.py (data-continuiti). Do not hand-edit — regenerate from
 * the sheet export the same way the source tool does.
 */

export interface JourneyMilestone {
  stage: number;
  name: string;
  purpose: string;
  notes: string;
  signal: string;
}

export interface LibraryMilestone extends JourneyMilestone {
  section: string;
  when: string;
}

export interface JourneyTemplate {
  title: string;
  clientType: string | null;
  milestones: JourneyMilestone[];
}

export interface VerticalInfo {
  key: string;
  label: string;
  tab: string | null;
  defaultType: string;
  iab: string[];
}

export const STAGES: Record<number, string> = {
  '1': 'Awareness/Discovery',
  '2': 'Consideration/Inquiry',
  '3': 'Conversion/Sales',
  '4': 'Post-Sale/Fulfillment',
  '5': 'Repeat/Return'
};

export const CLIENT_TYPES: Record<string, string> = {
  ecomm: 'eComm',
  leadgen: 'Lead Gen',
  ecomm_sub: 'eComm w/ Subscription',
  ecomm_store: 'eComm w/ In-Store',
  hybrid: 'Hybrid (eComm + Lead Gen)'
};

export const VERTICALS: Record<string, VerticalInfo> = {
  fashion: {
    key: 'fashion',
    label: 'Style, Fashion & Beauty',
    tab: 'JV Fashion',
    defaultType: 'ecomm',
    iab: ['IAB18']
  },
  health: {
    key: 'health',
    label: 'Health, Wellness & Fitness',
    tab: 'JV Health',
    defaultType: 'ecomm_sub',
    iab: ['IAB7']
  },
  home_svcs: {
    key: 'home_svcs',
    label: 'Home & Garden - Home Services',
    tab: 'JV Home Svcs',
    defaultType: 'leadgen',
    iab: ['IAB10']
  },
  furniture: {
    key: 'furniture',
    label: 'Home & Garden - Furnishings',
    tab: 'JV Furniture',
    defaultType: 'ecomm_store',
    iab: ['IAB10']
  },
  food_bev: {
    key: 'food_bev',
    label: 'Food & Beverage (CPG / DTC)',
    tab: 'JV Food Bev',
    defaultType: 'ecomm_sub',
    iab: ['IAB8']
  },
  sports: {
    key: 'sports',
    label: 'Sporting Goods & Outdoor',
    tab: 'JV Sports',
    defaultType: 'ecomm',
    iab: ['IAB17']
  },
  b2b: {
    key: 'b2b',
    label: 'Business Services / B2B',
    tab: 'JV B2B',
    defaultType: 'leadgen',
    iab: ['IAB19', 'IAB3']
  },
  media: {
    key: 'media',
    label: 'Arts & Entertainment / Media',
    tab: 'JV Media',
    defaultType: 'ecomm_sub',
    iab: ['IAB1', 'IAB12', 'IAB9']
  },
  finance: {
    key: 'finance',
    label: 'Personal Finance / Fintech',
    tab: 'JV Finance',
    defaultType: 'leadgen',
    iab: ['IAB13']
  },
  family: {
    key: 'family',
    label: 'Baby, Kids & Family',
    tab: 'JV Family',
    defaultType: 'ecomm',
    iab: ['IAB6']
  },
  pets: {
    key: 'pets',
    label: 'Pet Food & Supplies',
    tab: 'JV Pets',
    defaultType: 'ecomm_sub',
    iab: ['IAB16']
  },
  auto: {
    key: 'auto',
    label: 'Automotive Parts & Services',
    tab: 'JV Auto',
    defaultType: 'hybrid',
    iab: ['IAB2']
  },
  marketplace: {
    key: 'marketplace',
    label: 'Shopping / Marketplaces',
    tab: 'JV Marketplace',
    defaultType: 'ecomm',
    iab: ['IAB22']
  },
  education: {
    key: 'education',
    label: 'Education & Training',
    tab: 'JV Education',
    defaultType: 'leadgen',
    iab: ['IAB5']
  },
  travel: {
    key: 'travel',
    label: 'Travel & Hospitality',
    tab: 'JV Travel',
    defaultType: 'ecomm',
    iab: ['IAB20']
  },
  other: {
    key: 'other',
    label: 'Other / Non-standard',
    tab: null,
    defaultType: 'ecomm',
    iab: []
  }
};

export const TEMPLATES: Record<string, JourneyTemplate> = {
  'JT eComm': {
    title: 'User Journey - eComm (Standard DTC) (Archetype)',
    clientType: 'ecomm',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch and source of demand.',
        notes:
          'User is served a paid social, search, display, or video ad; impression and click captured for attribution.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on the site after clicking a paid ad, organic listing, email, or direct entry; UTMs and click IDs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Initial Engagement on Website',
        purpose: 'Gauge user interest and intent.',
        notes:
          'User browses collections, views 2+ pages, or engages with content (lookbooks, guides, reviews).',
        signal: 'Engaged Session / Page Depth'
      },
      {
        stage: 2,
        name: 'Product Detail View',
        purpose: 'Identify products of interest for remarketing and merchandising.',
        notes:
          'User views one or more product detail pages; item-level data captured for dynamic remarketing.',
        signal: 'view_item'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned, addressable audience.',
        notes:
          'User submits email/phone via welcome offer, pop-up, or footer signup; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart',
        purpose: 'Signal strong purchase intent; power abandonment flows.',
        notes: 'User adds one or more items to cart; cart contents synced to email/SMS platform.',
        signal: 'add_to_cart'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger checkout-abandonment recovery.',
        notes:
          'User initiates checkout; shipping/payment steps tracked as sub-events where available.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'Purchase Completed',
        purpose: 'Core conversion; feeds ROAS, LTV, and bidding signals.',
        notes:
          'User completes an order; transaction ID, revenue, items, and new-vs-returning flag captured; server-side event mirrored.',
        signal: 'purchase'
      },
      {
        stage: 3,
        name: 'Account Created',
        purpose: 'Tie transactions to a durable customer identity.',
        notes:
          'User creates an account at or after checkout; user_id set for cross-device stitching.',
        signal: 'sign_up / user_id set'
      },
      {
        stage: 4,
        name: 'Order Fulfillment & Delivery',
        purpose: 'Monitor post-purchase experience that drives repeat rates.',
        notes:
          'Shipping confirmation, delivery confirmation, and tracking engagement captured from OMS/ESP.',
        signal: 'Delivery Confirmed (offline/ESP)'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate social proof and satisfaction signal.',
        notes:
          'Post-delivery flow requests a review or photo; submission tracked and rating captured.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Purchase',
        purpose: 'Measure retention and true customer LTV.',
        notes: 'Returning customer places a second (or later) order; repeat-rate cohort reporting.',
        signal: 'purchase (returning customer)'
      },
      {
        stage: 5,
        name: 'Loyalty Program Enrollment',
        purpose: 'Deepen retention economics and identity resolution.',
        notes: 'User joins loyalty/rewards program; points activity tracked.',
        signal: 'Loyalty Signup'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn customers into an acquisition channel.',
        notes:
          'User shares a referral link or code; referred-visit and referred-purchase attributed.',
        signal: 'Referral Link Share / Redemption'
      }
    ]
  },
  'JT Lead Gen': {
    title: 'User Journey - Lead Gen (Services) (Archetype)',
    clientType: 'leadgen',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch and source of demand.',
        notes:
          'User is served a paid search, social, LSA, or display ad; impression and click captured.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on the website after clicking an ad, organic result, or map listing.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Initial Engagement on Website',
        purpose: 'Gauge user interest and intent.',
        notes:
          'User browses service pages, views galleries/portfolios, reads testimonials, or checks service areas.',
        signal: 'Engaged Session / Key Page View'
      },
      {
        stage: 2,
        name: 'Call from Ad',
        purpose: 'Track direct phone inquiries initiated from ads.',
        notes:
          'User directly calls from a Google or Meta ad (call extension / click-to-call); call tracking assigns campaign.',
        signal: 'Click-to-Call (Ad)'
      },
      {
        stage: 2,
        name: 'Call from Website',
        purpose: 'Track phone inquiries and link them to acquisition campaigns.',
        notes: 'User calls a dynamically inserted tracking number displayed on the site.',
        signal: 'Phone Number Click / DNI Call'
      },
      {
        stage: 2,
        name: 'Quote / Estimate Tool Engagement',
        purpose: 'Capture high-intent research behavior.',
        notes: 'User starts a pricing calculator, estimate tool, or service configurator.',
        signal: 'Tool Start (form_start)'
      },
      {
        stage: 2,
        name: 'Chat Engagement',
        purpose: 'Capture and qualify conversational inquiries.',
        notes: 'User opens live chat or chatbot; conversation and contact info passed to CRM.',
        signal: 'Chat Started'
      },
      {
        stage: 3,
        name: 'Lead Form Submitted',
        purpose: 'Primary online conversion; entry point of the sales pipeline.',
        notes:
          'User submits a contact/quote request form; lead created in CRM with full source attribution (GCLID/FBCLID).',
        signal: 'generate_lead / Form Submit'
      },
      {
        stage: 3,
        name: 'Appointment / Consultation Booked',
        purpose: 'Progress lead to a scheduled sales interaction.',
        notes:
          'User books an appointment via scheduler or is scheduled by the call center; booking synced to CRM.',
        signal: 'Appointment Booked'
      },
      {
        stage: 3,
        name: 'Qualified Lead (MQL to SQL)',
        purpose: 'Filter lead quality; feed value-based bidding.',
        notes:
          'Sales/call center qualifies the lead; stage change imported back to ad platforms as an enhanced/offline conversion.',
        signal: 'CRM Stage Change (offline import)'
      },
      {
        stage: 3,
        name: 'Sale / Contract Closed',
        purpose: 'Ultimate conversion; ties revenue back to media.',
        notes:
          'Deal is closed-won in CRM; job value imported to ad platforms for value optimization.',
        signal: 'Closed-Won (offline conversion)'
      },
      {
        stage: 4,
        name: 'Service Scheduled & Fulfilled',
        purpose: 'Monitor fulfillment experience that drives reviews and referrals.',
        notes:
          'Job/service is scheduled and completed; completion status synced from field-service or CRM system.',
        signal: 'Job Completed (CRM)'
      },
      {
        stage: 4,
        name: 'Review Request',
        purpose: 'Generate ratings that power local SEO and social proof.',
        notes: 'Post-service flow requests Google/third-party reviews; submissions monitored.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Service / Maintenance Plan',
        purpose: 'Measure retention and recurring revenue.',
        notes: 'Customer books an additional service or enrolls in a maintenance/service plan.',
        signal: 'Repeat Booking / Plan Enrollment'
      },
      {
        stage: 5,
        name: 'Referral',
        purpose: 'Turn satisfied customers into an acquisition channel.',
        notes: 'Customer submits a referral; referred lead attributed to the program.',
        signal: 'Referral Submitted'
      }
    ]
  },
  'JT eComm Sub': {
    title: 'User Journey - eComm w/ Subscription (Archetype)',
    clientType: 'ecomm_sub',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch and source of demand.',
        notes:
          'User is served paid social/search/video creative, often education- or problem-led; click IDs captured.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on the site or a landing page funnel; UTMs and click IDs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Educational Content Engagement',
        purpose: 'Build problem/solution awareness; seed retargeting pools.',
        notes: 'User engages with articles, ingredient/benefit pages, or comparison content.',
        signal: 'Content Engagement'
      },
      {
        stage: 2,
        name: 'Quiz / Plan Builder Completed',
        purpose: 'Capture zero-party data for personalization and qualification.',
        notes:
          'User completes a product-match quiz or plan builder; responses stored against profile.',
        signal: 'Quiz Complete'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned, addressable audience.',
        notes: 'User submits email/phone via quiz, offer, or pop-up; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart (Plan Selected)',
        purpose: 'Signal purchase intent and capture one-time vs. subscribe choice.',
        notes:
          'User adds to cart choosing one-time purchase or subscribe-and-save plan; selection captured as item parameter.',
        signal: 'add_to_cart (purchase_type)'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User initiates checkout; plan/frequency parameters carried through.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'First Purchase - One-Time',
        purpose: 'Convert buyers not yet ready for subscription.',
        notes: 'User completes a one-time order; flagged for subscription-upsell flows.',
        signal: 'purchase (one-time)'
      },
      {
        stage: 3,
        name: 'Subscription Started',
        purpose: 'Core conversion; begins recurring revenue relationship.',
        notes:
          'User completes first subscription order or converts a trial; plan, frequency, and discount captured; server-side event mirrored.',
        signal: 'subscription_start / purchase'
      },
      {
        stage: 4,
        name: 'First Delivery & Onboarding',
        purpose: 'Drive successful first use, the biggest churn lever.',
        notes: 'First shipment delivered; onboarding/education flow engagement tracked.',
        signal: 'Delivery Confirmed / Flow Engagement'
      },
      {
        stage: 4,
        name: 'Subscription Management Action',
        purpose: 'Detect churn risk early; enable save-flows.',
        notes:
          'User skips, pauses, swaps, or changes frequency in the customer portal; events synced from subscription platform.',
        signal: 'skip/pause/swap (sub platform)'
      },
      {
        stage: 5,
        name: 'Recurring Order (Rebill)',
        purpose: 'Measure retained revenue and cohort health.',
        notes: 'Subscription renews successfully; charge count and cohort month tracked.',
        signal: 'recurring_purchase'
      },
      {
        stage: 5,
        name: 'Cancellation & Winback',
        purpose: 'Quantify churn and recover lapsed subscribers.',
        notes: 'User cancels; exit-survey reason captured; winback flows and reactivation tracked.',
        signal: 'subscription_cancel / reactivation'
      },
      {
        stage: 5,
        name: 'Upgrade / Cross-Sell',
        purpose: 'Grow subscriber LTV.',
        notes: 'Subscriber adds products, upgrades plan, or increases frequency.',
        signal: 'Plan Upgrade / Add-On Purchase'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn subscribers into an acquisition channel.',
        notes: 'Subscriber shares referral link; referred signups and subscriptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JT eComm Store': {
    title: 'User Journey - eComm w/ In-Store (Archetype)',
    clientType: 'ecomm_store',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure (National + Local)',
        purpose: 'Establish first touch across DTC and store-level demand.',
        notes:
          'User is served national DTC creative or geo-targeted local campaigns (PMax for store goals, Local Inventory Ads).',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on the site from paid, organic, or local listings; UTMs and click IDs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Store Locator View',
        purpose: 'Identify omnichannel intent early.',
        notes: 'User views the store locator or a store detail page; store ID captured.',
        signal: 'store_locator_view'
      },
      {
        stage: 2,
        name: 'Product Detail View w/ Local Availability',
        purpose: 'Connect product interest to nearby inventory.',
        notes: 'User views a PDP and checks in-store availability / local inventory feed.',
        signal: 'view_item + availability_check'
      },
      {
        stage: 2,
        name: 'Get Directions / Click-to-Call Store',
        purpose: 'Capture strong store-visit intent.',
        notes: 'User clicks directions or calls a store from the site or Google Business Profile.',
        signal: 'get_directions / click_to_call'
      },
      {
        stage: 2,
        name: 'Add to Cart',
        purpose: 'Signal online purchase intent.',
        notes: 'User adds items to cart; fulfillment preference (ship vs. pickup) not yet chosen.',
        signal: 'add_to_cart'
      },
      {
        stage: 2,
        name: 'Appointment / Consultation Booked',
        purpose: 'Bridge digital interest to in-store selling (fittings, design consults).',
        notes:
          'User books an in-store appointment or virtual consultation; booking synced to store systems.',
        signal: 'Appointment Booked'
      },
      {
        stage: 3,
        name: 'Online Purchase (Ship-to-Home)',
        purpose: 'Core DTC conversion.',
        notes:
          'User completes an order for delivery; transaction and items captured; server-side mirrored.',
        signal: 'purchase (ship)'
      },
      {
        stage: 3,
        name: 'BOPIS / Pickup Order Placed',
        purpose: 'Capture omnichannel conversions that drive store traffic.',
        notes:
          'User completes a buy-online-pickup-in-store or curbside order; store ID attached to transaction.',
        signal: 'purchase (pickup, store_id)'
      },
      {
        stage: 3,
        name: 'In-Store Purchase (Matched)',
        purpose: 'Close the loop on offline revenue driven by digital media.',
        notes:
          'POS transaction matched to digital identity via loyalty, email, or card match; imported as offline conversion; store-visit conversions monitored.',
        signal: 'Offline Conversion Import / Store Visit'
      },
      {
        stage: 4,
        name: 'Pickup Completed / Delivery',
        purpose: 'Monitor fulfillment experience across channels.',
        notes: 'Order picked up or delivered; confirmation events from OMS.',
        signal: 'Pickup Confirmed / Delivery Confirmed'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate social proof across product and store listings.',
        notes: 'Post-purchase flow requests product and store (GBP) reviews.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Purchase (Any Channel)',
        purpose: 'Measure omnichannel retention and LTV.',
        notes: 'Customer purchases again online or in-store; unified via customer ID.',
        signal: 'purchase (returning, any channel)'
      },
      {
        stage: 5,
        name: 'Loyalty Program Enrollment & Activity',
        purpose: 'Strengthen identity resolution across channels.',
        notes:
          'User joins loyalty program; in-store scans tie POS activity to the digital profile.',
        signal: 'Loyalty Signup / Scan'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn customers into an acquisition channel.',
        notes: 'Customer refers friends; redemptions attributed online and in-store.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JT Hybrid': {
    title: 'User Journey - Hybrid (eComm + Lead Gen) (Archetype)',
    clientType: 'hybrid',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch across both self-serve and assisted demand.',
        notes:
          'User served paid search/social/video; campaigns segmented by product (shopping/PMax) vs. service/quote intent from the start.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, or referral; UTMs and click IDs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Path Identified (Self-Serve vs. Assisted)',
        purpose:
          'Segment users early into buy-online vs. quote/consult paths - the defining hybrid signal.',
        notes:
          'First meaningful navigation classifies intent: product/category pages (self-serve) vs. services, pricing-request, or consultation pages (assisted); path stored as user property.',
        signal: 'Key Page View (path_type)'
      },
      {
        stage: 2,
        name: 'Product Detail View (Self-Serve Path)',
        purpose: 'Identify products of interest for dynamic remarketing.',
        notes:
          'User views transactional PDPs (standard SKUs, parts, consumables); item data captured.',
        signal: 'view_item'
      },
      {
        stage: 2,
        name: 'Add to Cart (Self-Serve Path)',
        purpose: 'Signal online purchase intent; power abandonment flows.',
        notes: 'User adds purchasable items to cart.',
        signal: 'add_to_cart'
      },
      {
        stage: 2,
        name: 'Quote / Estimate Request Started (Assisted Path)',
        purpose: 'Capture entry into the lead funnel for complex, custom, or high-ticket needs.',
        notes:
          'User starts a quote form, project configurator, or consultation scheduler; partial fills tracked.',
        signal: 'form_start'
      },
      {
        stage: 2,
        name: 'Call from Ad / Website (Assisted Path)',
        purpose: 'Track phone inquiries and link them to acquisition campaigns.',
        notes:
          'User calls from ad extensions or dynamically inserted site numbers; call outcomes scored and routed.',
        signal: 'Click-to-Call / DNI Call'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture (Both Paths)',
        purpose: 'Convert anonymous traffic into an owned audience regardless of path.',
        notes:
          'User signs up via offer, content gate, or quote flow; path type carried into ESP for divergent nurture tracks.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Chat Engagement (Path Router)',
        purpose: 'Qualify and route conversational inquiries to the right path.',
        notes:
          'Chat/chatbot triages: simple needs pushed to checkout, complex needs converted to leads with contact capture.',
        signal: 'Chat Started (routed_outcome)'
      },
      {
        stage: 3,
        name: 'Online Purchase (Self-Serve Conversion)',
        purpose: 'Core eComm conversion for standard products.',
        notes:
          'User completes an online order; transaction, items, and new-vs-returning captured; server-side mirrored.',
        signal: 'purchase'
      },
      {
        stage: 3,
        name: 'Lead Form Submitted (Assisted Conversion)',
        purpose: 'Core lead-gen conversion for complex/custom needs.',
        notes:
          'Quote or consultation request submitted; lead created in CRM with GCLID/FBCLID for offline import.',
        signal: 'generate_lead / Form Submit'
      },
      {
        stage: 3,
        name: 'Consultation Completed / Quote Delivered',
        purpose: 'Progress assisted leads through the sales pipeline.',
        notes:
          'Rep completes consult and issues quote; stage synced to ad platforms as offline conversion.',
        signal: 'CRM Stage Change (offline import)'
      },
      {
        stage: 3,
        name: 'Assisted Sale Closed-Won',
        purpose: 'Ultimate assisted-path conversion; ties offline revenue to media.',
        notes:
          'Deal closed in CRM; contract/order value imported for value-based bidding alongside online purchase values.',
        signal: 'Closed-Won (offline conversion)'
      },
      {
        stage: 4,
        name: 'Fulfillment (Shipment or Installation)',
        purpose: 'Monitor delivery experience across both paths.',
        notes:
          'Self-serve orders ship; assisted sales schedule installation/service; completion synced from OMS/FSM.',
        signal: 'Delivery Confirmed / Job Completed'
      },
      {
        stage: 4,
        name: 'Review Request (Both Paths)',
        purpose: 'Generate social proof for products and service quality.',
        notes:
          'Post-fulfillment flow requests product and Google/service reviews as appropriate to path.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Purchase / Repeat Service',
        purpose: 'Measure retention on each path separately.',
        notes:
          'Returning customer orders again online or books additional service/projects; path-level repeat rates reported.',
        signal: 'purchase (returning) / Repeat Booking'
      },
      {
        stage: 5,
        name: 'Cross-Path Conversion',
        purpose: 'Measure the hybrid models compounding value - the key blended-LTV metric.',
        notes:
          'Assisted customer later buys consumables/parts online, or an online buyer converts to an installed/service relationship; movement between paths tracked via unified customer ID.',
        signal: 'purchase or lead (opposite path)'
      },
      {
        stage: 5,
        name: 'Maintenance Plan / Reorder Program Enrollment',
        purpose: 'Convert either path into recurring revenue.',
        notes:
          'Customer enrolls in a maintenance plan (assisted) or auto-reorder of consumables (self-serve).',
        signal: 'Plan Enrollment / subscription_start'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn customers into an acquisition channel.',
        notes: 'Customer submits referral; referred lead or purchase attributed.',
        signal: 'Referral Submitted / Redemption'
      }
    ]
  },
  'JV Fashion': {
    title: 'User Journey - Style, Fashion & Beauty',
    clientType: 'ecomm',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; creative-led demand generation.',
        notes:
          'User served paid social/video creative (UGC, influencer, catalog ads) or branded/non-branded search.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on site from paid, organic, influencer link, or affiliate; UTMs/click IDs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Collection / Lookbook Browse',
        purpose: 'Gauge style interest; build category-level audiences.',
        notes: 'User browses category pages, new arrivals, lookbooks, or shade finders.',
        signal: 'view_item_list'
      },
      {
        stage: 2,
        name: 'Product Detail View',
        purpose: 'Identify products/variants of interest for dynamic remarketing.',
        notes: 'User views PDPs; color/size/shade variants captured as item parameters.',
        signal: 'view_item'
      },
      {
        stage: 2,
        name: 'Size Guide / Try-On Tool Engagement',
        purpose: 'Capture fit-confidence behavior that predicts conversion and returns.',
        notes: 'User opens size guide, fit finder, or virtual try-on tool.',
        signal: 'Size Guide Open / Tool Start'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned audience.',
        notes: 'User signs up via welcome offer (e.g., 10-15% off) pop-up; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart / Wishlist',
        purpose: 'Signal strong purchase intent; power abandonment and back-in-stock flows.',
        notes:
          'User adds to cart or saves to wishlist; out-of-stock interest captured for restock alerts.',
        signal: 'add_to_cart / add_to_wishlist'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger checkout-abandonment recovery.',
        notes: 'User initiates checkout; discount-code usage tracked.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'Purchase Completed',
        purpose: 'Core conversion; feeds ROAS, LTV, and bidding signals.',
        notes:
          'Order completed with items, sizes, discount, and new-vs-returning flag; server-side event mirrored.',
        signal: 'purchase'
      },
      {
        stage: 3,
        name: 'Account Created',
        purpose: 'Tie transactions to a durable customer identity.',
        notes: 'User creates account at checkout; user_id set for cross-device stitching.',
        signal: 'sign_up / user_id set'
      },
      {
        stage: 4,
        name: 'Delivery & First Wear',
        purpose: 'Monitor post-purchase experience that drives repeat rates.',
        notes:
          'Shipping/delivery confirmations tracked; styling and care content engagement measured.',
        signal: 'Delivery Confirmed (OMS/ESP)'
      },
      {
        stage: 4,
        name: 'Return / Exchange',
        purpose: 'Measure the fit-driven return loop critical in apparel and beauty economics.',
        notes:
          'Return or exchange initiated; reason codes captured; exchange saves tracked vs. refunds.',
        signal: 'Return Initiated (OMS)'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate social proof (photos, fit feedback, shade reviews).',
        notes: 'Post-delivery flow requests review with fit/shade attributes.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Purchase',
        purpose: 'Measure retention across seasons and drops.',
        notes: 'Returning customer purchases again; seasonal cohort reporting.',
        signal: 'purchase (returning customer)'
      },
      {
        stage: 5,
        name: 'Loyalty / VIP Tier Activity',
        purpose: 'Deepen retention; power early access and drop strategies.',
        notes:
          'User joins loyalty/VIP program; tier progression and early-access engagement tracked.',
        signal: 'Loyalty Signup / Tier Change'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn customers into an acquisition channel.',
        notes: 'User shares referral link or posts UGC with brand tag; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Health': {
    title: 'User Journey - Health, Wellness & Fitness',
    clientType: 'ecomm_sub',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; education/problem-led creative.',
        notes:
          'User served paid social/video (founder story, clinical claims, before/after within policy) or condition/ingredient search.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit / Landing Page',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site or advertorial/listicle funnel; UTMs/click IDs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Science / Benefits Content Engagement',
        purpose: 'Build trust and problem-solution fit.',
        notes: 'User reads ingredient, clinical-evidence, or how-it-works content.',
        signal: 'Content Engagement'
      },
      {
        stage: 2,
        name: 'Health Quiz / Assessment Completed',
        purpose: 'Capture zero-party data for personalization and qualification.',
        notes:
          'User completes wellness quiz or assessment; goals and needs stored against profile (privacy-safe).',
        signal: 'Quiz Complete'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned audience.',
        notes:
          'User submits contact info via quiz results, offer, or content gate; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart (Plan Selected)',
        purpose: 'Signal purchase intent; capture one-time vs. subscribe choice.',
        notes:
          'User selects one-time or subscribe-and-save (typical 15-25% discount); selection captured as item parameter.',
        signal: 'add_to_cart (purchase_type)'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes:
          'User initiates checkout; HSA/FSA eligibility or payment options surfaced where applicable.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'First Purchase - One-Time',
        purpose: 'Convert buyers not yet ready to subscribe.',
        notes: 'One-time order completed; flagged for subscription-upsell flows.',
        signal: 'purchase (one-time)'
      },
      {
        stage: 3,
        name: 'Subscription Started',
        purpose: 'Core conversion; begins recurring revenue relationship.',
        notes:
          'First subscription order or trial conversion; plan/frequency captured; server-side mirrored.',
        signal: 'subscription_start / purchase'
      },
      {
        stage: 4,
        name: 'First Delivery & Onboarding',
        purpose: 'Drive successful first use and habit formation - the biggest churn lever.',
        notes:
          'First shipment delivered; onboarding content, app pairing (for devices/wearables), or dosage guides tracked.',
        signal: 'Delivery Confirmed / Flow Engagement'
      },
      {
        stage: 4,
        name: 'Adherence / Engagement Signal',
        purpose: 'Predict churn via usage behavior.',
        notes:
          'App opens, tracking logs, or refill-timing signals monitored where the product includes a digital component.',
        signal: 'App Engagement / Refill Timing'
      },
      {
        stage: 5,
        name: 'Recurring Order (Rebill)',
        purpose: 'Measure retained revenue and cohort health.',
        notes: 'Subscription renews; charge count and cohort month tracked.',
        signal: 'recurring_purchase'
      },
      {
        stage: 5,
        name: 'Cancellation & Winback',
        purpose: 'Quantify churn; recover lapsed subscribers.',
        notes: 'Cancel with exit-survey reason; save-offers and reactivations tracked.',
        signal: 'subscription_cancel / reactivation'
      },
      {
        stage: 5,
        name: 'Upgrade / Cross-Sell',
        purpose: 'Grow subscriber LTV across the product system.',
        notes: 'Subscriber adds complementary SKUs (stack building) or upgrades plan.',
        signal: 'Add-On Purchase / Plan Upgrade'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn subscribers into an acquisition channel.',
        notes: 'Subscriber shares referral link; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Home Svcs': {
    title: 'User Journey - Home Services & Improvement',
    clientType: 'leadgen',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch in a high-consideration, local category.',
        notes:
          'User served paid search (service + geo terms), LSAs, paid social, or sees direct mail/OOH driving branded search.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on site or location page after clicking an ad, organic result, or map listing.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Initial Engagement on Website',
        purpose: 'Gauge project interest and intent.',
        notes:
          'User browses service pages, project galleries, before/after photos, financing info, and service-area pages.',
        signal: 'Engaged Session / Key Page View'
      },
      {
        stage: 2,
        name: 'Call from Ad',
        purpose: 'Track direct phone inquiries initiated from ads.',
        notes:
          'User calls directly from a Google/Meta ad or LSA; call tracking assigns campaign and records qualification.',
        signal: 'Click-to-Call (Ad)'
      },
      {
        stage: 2,
        name: 'Call from Website',
        purpose: 'Track phone inquiries and link them to acquisition campaigns.',
        notes: 'User calls a dynamically inserted tracking number on the site.',
        signal: 'Phone Number Click / DNI Call'
      },
      {
        stage: 2,
        name: 'Estimate Tool / Financing Check',
        purpose: 'Capture high-intent research behavior.',
        notes:
          'User starts an instant-quote tool, project configurator, or financing pre-qualification.',
        signal: 'Tool Start (form_start)'
      },
      {
        stage: 2,
        name: 'Chat Engagement',
        purpose: 'Capture and qualify conversational inquiries.',
        notes: 'User engages chat/chatbot; contact info and project details passed to CRM.',
        signal: 'Chat Started'
      },
      {
        stage: 3,
        name: 'Lead Form Submitted (Estimate Request)',
        purpose: 'Primary online conversion; entry point of the sales pipeline.',
        notes:
          'User submits a free-estimate request; lead created in CRM with GCLID/FBCLID for offline import.',
        signal: 'generate_lead / Form Submit'
      },
      {
        stage: 3,
        name: 'In-Home Consultation Scheduled',
        purpose: 'Progress lead to the critical sales appointment.',
        notes: 'Consultation booked via scheduler or call center; set-rate tracked by source.',
        signal: 'Appointment Booked'
      },
      {
        stage: 3,
        name: 'Quote Delivered / Qualified',
        purpose: 'Filter lead quality; feed value-based bidding.',
        notes:
          'Rep completes consult and delivers quote; demo-sat and quote stages imported to ad platforms as offline conversions.',
        signal: 'CRM Stage Change (offline import)'
      },
      {
        stage: 3,
        name: 'Contract Signed',
        purpose: 'Ultimate conversion; ties project revenue back to media.',
        notes: 'Deal closed-won; contract value imported for value-based bidding.',
        signal: 'Closed-Won (offline conversion)'
      },
      {
        stage: 4,
        name: 'Project Scheduled & Completed',
        purpose: 'Monitor fulfillment experience that drives reviews and referrals.',
        notes: 'Installation/service completed; status synced from field-service system.',
        signal: 'Job Completed (CRM/FSM)'
      },
      {
        stage: 4,
        name: 'Review Request',
        purpose: 'Generate ratings that power local SEO and LSA rankings.',
        notes: 'Post-completion flow requests Google reviews; submissions and ratings monitored.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Service / Maintenance Plan',
        purpose: 'Measure retention and recurring revenue.',
        notes: 'Customer books additional projects or enrolls in a maintenance plan.',
        signal: 'Repeat Booking / Plan Enrollment'
      },
      {
        stage: 5,
        name: 'Referral',
        purpose: 'Turn satisfied homeowners into an acquisition channel.',
        notes: 'Customer submits referral (often incentivized); referred lead attributed.',
        signal: 'Referral Submitted'
      }
    ]
  },
  'JV Food Bev': {
    title: 'User Journey - Food & Beverage (CPG / DTC)',
    clientType: 'ecomm_sub',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; taste/benefit-led creative.',
        notes:
          'User served paid social/video (recipe, taste-test, lifestyle creative) or category search.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on site from paid, organic, influencer, or retail-media extension; UTMs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Product / Flavor Browse',
        purpose: 'Gauge interest; build flavor- and use-case-level audiences.',
        notes: 'User browses flavors, bundles, recipes, or nutrition information.',
        signal: 'view_item_list / Content Engagement'
      },
      {
        stage: 2,
        name: 'Store Locator / Where-to-Buy View',
        purpose: 'Capture retail-intent demand for omnichannel CPG brands.',
        notes:
          'User checks retail availability (grocery, club, marketplace); retailer clicks tracked.',
        signal: 'where_to_buy_click'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned audience.',
        notes: 'User signs up via first-order offer; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart (Bundle / Plan Selected)',
        purpose: 'Signal purchase intent; capture one-time vs. subscribe choice.',
        notes: 'User builds a bundle or variety pack and selects one-time vs. subscribe-and-save.',
        signal: 'add_to_cart (purchase_type)'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User initiates checkout; shipping-threshold nudges tracked.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'First Purchase - One-Time',
        purpose: 'Convert trial buyers not yet ready to subscribe.',
        notes: 'First one-time order completed; flagged for subscription-upsell flows.',
        signal: 'purchase (one-time)'
      },
      {
        stage: 3,
        name: 'Subscription Started',
        purpose: 'Core conversion; begins recurring consumable relationship.',
        notes:
          'First subscription order placed; SKU mix, frequency, and discount captured; server-side mirrored.',
        signal: 'subscription_start / purchase'
      },
      {
        stage: 4,
        name: 'First Delivery & First Taste',
        purpose: 'Drive successful first consumption - the key churn lever for consumables.',
        notes:
          'Delivery confirmed; recipe/usage content engagement and satisfaction check-in tracked.',
        signal: 'Delivery Confirmed / Flow Engagement'
      },
      {
        stage: 4,
        name: 'Subscription Management Action',
        purpose: 'Detect churn risk early; enable save-flows.',
        notes: 'User skips, pauses, swaps flavors, or changes cadence in the portal.',
        signal: 'skip/pause/swap (sub platform)'
      },
      {
        stage: 5,
        name: 'Recurring Order (Rebill)',
        purpose: 'Measure retained revenue and cohort health.',
        notes: 'Subscription renews; consumption-matched cadence health monitored.',
        signal: 'recurring_purchase'
      },
      {
        stage: 5,
        name: 'Cancellation & Winback',
        purpose: 'Quantify churn; recover lapsed subscribers.',
        notes: 'Cancel with reason capture; winback offers and reactivations tracked.',
        signal: 'subscription_cancel / reactivation'
      },
      {
        stage: 5,
        name: 'Cross-Sell / New Flavor Adoption',
        purpose: 'Grow LTV through assortment expansion.',
        notes: 'Subscriber adds new flavors/products or upsizes the box.',
        signal: 'Add-On Purchase'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn fans into an acquisition channel.',
        notes: 'Customer shares referral or gift; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Sports': {
    title: 'User Journey - Sporting Goods & Outdoor',
    clientType: 'ecomm',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; performance/lifestyle creative.',
        notes: 'User served paid social/video (athlete, product tech) or gear-related search.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, affiliate, or community content.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Category / Gear Guide Browse',
        purpose: 'Gauge sport and skill-level interest.',
        notes:
          'User browses categories, buying guides, or comparison content; sport-level audience segmentation.',
        signal: 'view_item_list / Content Engagement'
      },
      {
        stage: 2,
        name: 'Product Detail View',
        purpose: 'Identify products of interest for dynamic remarketing.',
        notes: 'User views PDPs; spec and size variants captured.',
        signal: 'view_item'
      },
      {
        stage: 2,
        name: 'Fitting / Selector Tool Engagement',
        purpose: 'Capture fit-confidence behavior (club fitting, board size, shoe finder).',
        notes: 'User uses a fit or selector tool; results stored for personalization.',
        signal: 'Tool Start / Complete'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned audience.',
        notes: 'User signs up via offer or gear-guide gate; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart',
        purpose: 'Signal strong purchase intent; power abandonment flows.',
        notes: 'User adds gear to cart; high-AOV items flagged for financing messaging.',
        signal: 'add_to_cart'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User initiates checkout; financing (e.g., installments) selection tracked.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'Purchase Completed',
        purpose: 'Core conversion; feeds ROAS, LTV, and bidding signals.',
        notes: 'Order completed; items, revenue, new-vs-returning captured; server-side mirrored.',
        signal: 'purchase'
      },
      {
        stage: 3,
        name: 'Dealer / Retail Referral (if applicable)',
        purpose: 'Capture demand routed to dealer networks.',
        notes: 'User clicks through to an authorized dealer or checks local availability.',
        signal: 'dealer_locator_click'
      },
      {
        stage: 4,
        name: 'Delivery & First Use',
        purpose: 'Monitor post-purchase experience; drive setup success.',
        notes:
          'Delivery confirmed; setup/tutorial content engagement and warranty registration tracked.',
        signal: 'Delivery Confirmed / Registration'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate social proof with sport-specific context.',
        notes: 'Post-delivery flow requests review; action photos encouraged.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Seasonal Repeat Purchase',
        purpose: 'Measure retention across seasons and gear-replacement cycles.',
        notes: 'Returning customer buys again (consumables, apparel, upgrades); seasonal cohorts.',
        signal: 'purchase (returning customer)'
      },
      {
        stage: 5,
        name: 'Loyalty / Community Enrollment',
        purpose: 'Deepen retention via community, events, and rewards.',
        notes: 'User joins loyalty program, community, or events list.',
        signal: 'Loyalty Signup'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn enthusiasts into an acquisition channel.',
        notes: 'Customer shares referral; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV B2B': {
    title: 'User Journey - B2B Software & Business Services',
    clientType: 'leadgen',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch with target accounts and ICP audiences.',
        notes:
          'User served LinkedIn/paid search/review-site placements; account-based matched audiences where available.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source and account.',
        notes:
          'User lands on site; UTMs captured; reverse-IP/account identification enriches firmographics.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Solution / Use-Case Content Engagement',
        purpose: 'Gauge problem fit; build intent-based audiences.',
        notes:
          'User reads product, solution, comparison, or pricing pages; content scoring feeds MAP.',
        signal: 'Key Page View / Content Score'
      },
      {
        stage: 2,
        name: 'Gated Content Download',
        purpose: 'Capture early-stage leads for nurture.',
        notes: 'User downloads a whitepaper, report, or template; MQL scoring begins.',
        signal: 'Content Download (generate_lead)'
      },
      {
        stage: 2,
        name: 'Webinar / Event Registration',
        purpose: 'Capture mid-funnel engagement.',
        notes: 'User registers for a webinar or event; attendance tracked separately.',
        signal: 'Event Registration'
      },
      {
        stage: 2,
        name: 'Free Trial / Freemium Signup',
        purpose: 'Capture product-led intent.',
        notes: 'User creates a free account; product analytics begin tracking activation behavior.',
        signal: 'sign_up (trial_start)'
      },
      {
        stage: 2,
        name: 'Demo Request Started',
        purpose: 'Identify hand-raisers entering the sales funnel.',
        notes:
          'User starts the demo/contact form; partial submissions tracked for abandonment follow-up.',
        signal: 'form_start'
      },
      {
        stage: 3,
        name: 'Demo Request Submitted (MQL)',
        purpose: 'Primary conversion; entry point of the pipeline.',
        notes: 'Form submitted; lead created in CRM/MAP with full attribution; routed to SDR.',
        signal: 'generate_lead / Form Submit'
      },
      {
        stage: 3,
        name: 'Sales Qualified (SQL / Opportunity Created)',
        purpose: 'Filter lead quality; feed value-based bidding.',
        notes:
          'SDR qualifies and opportunity created; stage synced to ad platforms via offline conversions/CAPI.',
        signal: 'CRM Stage Change (offline import)'
      },
      {
        stage: 3,
        name: 'Product Qualified (PQL) (if PLG)',
        purpose: 'Identify trial users showing buying signals.',
        notes: 'Trial account hits activation/usage thresholds; PQL flag synced to CRM.',
        signal: 'Product Usage Threshold'
      },
      {
        stage: 3,
        name: 'Closed-Won',
        purpose: 'Ultimate conversion; ties contract value back to media.',
        notes: 'Deal closed; ACV imported to ad platforms for value optimization.',
        signal: 'Closed-Won (offline conversion)'
      },
      {
        stage: 4,
        name: 'Onboarding & Activation',
        purpose: 'Drive time-to-value that determines renewal.',
        notes:
          'Implementation milestones and product activation metrics tracked (seats active, key features used).',
        signal: 'Activation Milestones (product analytics)'
      },
      {
        stage: 4,
        name: 'Adoption / Health Score',
        purpose: 'Predict churn and expansion readiness.',
        notes: 'Usage-based health score maintained in CS platform.',
        signal: 'Health Score Change'
      },
      {
        stage: 5,
        name: 'Renewal',
        purpose: 'Measure retained ARR.',
        notes: 'Contract renews; GRR/NRR reporting.',
        signal: 'Renewal Closed (CRM)'
      },
      {
        stage: 5,
        name: 'Expansion / Upsell',
        purpose: 'Grow account value.',
        notes: 'Seats, tiers, or modules added; expansion opportunity tracked.',
        signal: 'Expansion Closed-Won'
      },
      {
        stage: 5,
        name: 'Advocacy (Review / Referral / Case Study)',
        purpose: 'Turn customers into pipeline sources.',
        notes:
          'Customer leaves G2/Capterra review, refers a peer, or participates in a case study.',
        signal: 'Review / Referral Submitted'
      }
    ]
  },
  'JV Finance': {
    title: 'User Journey - Financial Services & Fintech',
    clientType: 'leadgen',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch under compliant creative.',
        notes:
          'User served paid search (high-intent rate/product terms), paid social, or comparison-site placements.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on site/landing page; UTMs captured; compliance-safe analytics configuration (no sensitive data).',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Product Education Engagement',
        purpose: 'Gauge product interest (loans, cards, insurance, investing).',
        notes: 'User engages calculators, rate tables, or explainer content.',
        signal: 'Content Engagement / Calculator Start'
      },
      {
        stage: 2,
        name: 'Rate Quote / Pre-Qualification Started',
        purpose: 'Capture high-intent shoppers with soft-pull tools.',
        notes: 'User starts a rate-check or pre-qualification flow (no hard credit pull).',
        signal: 'form_start (prequal)'
      },
      {
        stage: 2,
        name: 'Rate Quote Completed',
        purpose: 'Deliver personalized offer; strong bidding signal.',
        notes: 'Soft-pull quote returned; offer viewed; email/phone captured with consent.',
        signal: 'Prequal Complete (generate_lead)'
      },
      {
        stage: 2,
        name: 'Email / Phone Capture (Nurture)',
        purpose: 'Convert researchers into an addressable audience.',
        notes: 'User subscribes to rate alerts or saves progress; consent captured.',
        signal: 'Signup Form Submit'
      },
      {
        stage: 3,
        name: 'Application Started',
        purpose: 'Entry into the core conversion funnel.',
        notes: 'User begins the full application; step-level funnel tracked for drop-off analysis.',
        signal: 'application_start'
      },
      {
        stage: 3,
        name: 'Application Submitted',
        purpose: 'Primary online conversion.',
        notes:
          'Completed application submitted; application ID linked to click IDs for offline import.',
        signal: 'application_submit'
      },
      {
        stage: 3,
        name: 'Approved',
        purpose: 'Quality signal for value-based optimization.',
        notes:
          'Underwriting decision returned; approval imported as offline conversion (no sensitive attributes passed).',
        signal: 'Approval (offline import)'
      },
      {
        stage: 3,
        name: 'Funded / Account Opened',
        purpose: 'Ultimate conversion; ties booked revenue to media.',
        notes:
          'Loan funded, policy bound, or account opened and funded; value imported for bidding.',
        signal: 'Funded / Account Opened (offline)'
      },
      {
        stage: 4,
        name: 'Activation & First Use',
        purpose: 'Drive early engagement that determines retention.',
        notes: 'First transaction, card activation, first deposit, or autopay setup tracked.',
        signal: 'First Transaction / Autopay Set'
      },
      {
        stage: 4,
        name: 'Servicing & App Engagement',
        purpose: 'Monitor relationship health.',
        notes: 'App logins, statement views, and support interactions monitored in aggregate.',
        signal: 'App Engagement'
      },
      {
        stage: 5,
        name: 'Cross-Sell (Second Product)',
        purpose: 'Grow customer lifetime value across the product suite.',
        notes: 'Existing customer opens an additional product; measured via CRM.',
        signal: 'Cross-Sell Conversion (CRM)'
      },
      {
        stage: 5,
        name: 'Renewal / Balance Retention',
        purpose: 'Measure retained book of business.',
        notes: 'Policy renews or balances retained/refinanced internally.',
        signal: 'Renewal (CRM)'
      },
      {
        stage: 5,
        name: 'Referral',
        purpose: 'Turn customers into an acquisition channel.',
        notes: 'Customer refers via incentivized program; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Pets': {
    title: 'User Journey - Pet Food & Supplies',
    clientType: 'ecomm_sub',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; pet-parent emotional creative.',
        notes:
          'User served paid social/video (pet transformation, vet-backed claims) or category search.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, or influencer content.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Educational Content Engagement',
        purpose: 'Build trust on nutrition/health claims.',
        notes: 'User reads ingredient, vet-review, or transition-guide content.',
        signal: 'Content Engagement'
      },
      {
        stage: 2,
        name: 'Pet Profile / Quiz Completed',
        purpose: 'Capture zero-party pet data for personalization.',
        notes:
          'User builds a pet profile (species, breed, age, weight, needs); powers portioning and recommendations.',
        signal: 'Quiz Complete (pet_profile)'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned audience.',
        notes: 'User signs up via offer or quiz results; consent captured.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart (Plan Selected)',
        purpose: 'Signal purchase intent; capture one-time vs. subscribe or trial-box choice.',
        notes: 'User selects trial box, one-time, or autoship plan; selection captured.',
        signal: 'add_to_cart (purchase_type)'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User initiates checkout.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'Trial Box / First Purchase',
        purpose: 'Convert new pet parents with low-risk entry offer.',
        notes: 'Trial or first one-time order completed; flagged for autoship conversion flows.',
        signal: 'purchase (trial/one-time)'
      },
      {
        stage: 3,
        name: 'Autoship Subscription Started',
        purpose: 'Core conversion; begins recurring consumable relationship.',
        notes: 'Autoship activated with portion-matched cadence; server-side mirrored.',
        signal: 'subscription_start'
      },
      {
        stage: 4,
        name: 'First Delivery & Food Transition',
        purpose: 'Drive successful transition - the key churn lever for pet food.',
        notes: 'Delivery confirmed; transition-guide engagement and satisfaction check-in tracked.',
        signal: 'Delivery Confirmed / Flow Engagement'
      },
      {
        stage: 4,
        name: 'Subscription Management Action',
        purpose: 'Detect churn risk early (over-supply is the top cancel reason).',
        notes: 'User skips, delays, or adjusts portions/cadence in the portal.',
        signal: 'skip/pause/adjust (sub platform)'
      },
      {
        stage: 5,
        name: 'Recurring Order (Rebill)',
        purpose: 'Measure retained revenue and cohort health.',
        notes: 'Autoship renews; cadence health vs. consumption monitored.',
        signal: 'recurring_purchase'
      },
      {
        stage: 5,
        name: 'Cancellation & Winback',
        purpose: 'Quantify churn; recover lapsed subscribers.',
        notes: 'Cancel with reason capture; winback and reactivation tracked.',
        signal: 'subscription_cancel / reactivation'
      },
      {
        stage: 5,
        name: 'Cross-Sell (Treats / Supplements / Insurance)',
        purpose: 'Grow LTV across the pet-care wallet.',
        notes: 'Subscriber adds complementary categories.',
        signal: 'Add-On Purchase'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn pet parents into an acquisition channel.',
        notes: 'Customer shares referral; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Family': {
    title: 'User Journey - Baby, Kids & Family',
    clientType: 'ecomm',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Reach parents at life-stage moments.',
        notes:
          'User served paid social (life-stage targeting, parenting creators) or category search (strollers, car seats, essentials).',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, registry referral, or content.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Buying Guide / Safety Content Engagement',
        purpose: 'Build trust in a research-heavy, safety-driven category.',
        notes: 'User reads buying guides, safety certifications, or comparison content.',
        signal: 'Content Engagement'
      },
      {
        stage: 2,
        name: 'Product Detail View',
        purpose: 'Identify products of interest; long research cycles expected.',
        notes: 'User views PDPs repeatedly across sessions; view-count recency feeds remarketing.',
        signal: 'view_item'
      },
      {
        stage: 2,
        name: 'Registry Creation / Add-to-Registry',
        purpose: 'Capture the category-defining life-stage conversion.',
        notes:
          'User creates a registry or adds items on-site or via partner registries; registry ID captured.',
        signal: 'registry_create / add_to_registry'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture (Life-Stage)',
        purpose: 'Convert researchers into an audience with due-date/age personalization.',
        notes: 'User signs up; optional due date or child age enables stage-based flows.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart',
        purpose: 'Signal purchase intent; power abandonment flows.',
        notes: 'User adds items to cart; bundle/completer suggestions tracked.',
        signal: 'add_to_cart'
      },
      {
        stage: 2,
        name: 'Checkout Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User initiates checkout.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'Purchase Completed (Self or Gift)',
        purpose: 'Core conversion; distinguish self-purchase vs. gifting.',
        notes:
          'Order completed; gift flag and registry attribution captured; server-side mirrored.',
        signal: 'purchase'
      },
      {
        stage: 3,
        name: 'Registry Completion Purchase',
        purpose: 'Capture the post-event completion-discount conversion.',
        notes: 'Registry owner buys remaining items with completion discount.',
        signal: 'purchase (registry_completion)'
      },
      {
        stage: 4,
        name: 'Delivery, Assembly & Registration',
        purpose: 'Monitor post-purchase experience; capture warranty/safety registration.',
        notes:
          'Delivery confirmed; assembly-guide engagement and product registration (recall contactability) tracked.',
        signal: 'Delivery Confirmed / Registration'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate parent social proof.',
        notes: 'Post-delivery flow requests review with child-age context.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Life-Stage Repeat Purchase',
        purpose: 'Measure retention as the child ages into new needs.',
        notes: 'Returning customer buys next-stage products (sizes, gear); age-based cohort flows.',
        signal: 'purchase (returning customer)'
      },
      {
        stage: 5,
        name: 'Loyalty / Community Enrollment',
        purpose: 'Deepen retention through parent community and rewards.',
        notes: 'User joins loyalty program or parenting content community.',
        signal: 'Loyalty Signup'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn parents into an acquisition channel (high word-of-mouth category).',
        notes: 'Customer refers other parents; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Auto': {
    title: 'User Journey - Automotive (Parts, Accessories & Services)',
    clientType: null,
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; fitment- and problem-led creative.',
        notes:
          'User served paid search (part + vehicle terms), shopping ads, or enthusiast social/video.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, forum, or marketplace referral.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Vehicle Selection (Year/Make/Model)',
        purpose: 'Capture the fitment context that defines the session.',
        notes: 'User selects or confirms vehicle; YMM stored to profile and all subsequent events.',
        signal: 'vehicle_select (YMM)'
      },
      {
        stage: 2,
        name: 'Fitment-Verified Product View',
        purpose: 'Identify compatible products of interest.',
        notes: 'User views PDPs with fitment confirmation; compatibility checks tracked.',
        signal: 'view_item (fitment_verified)'
      },
      {
        stage: 2,
        name: 'Install Guide / How-To Engagement',
        purpose: 'Capture DIY-confidence behavior that predicts conversion.',
        notes: 'User views installation guides or videos; DIY vs. install-service intent inferred.',
        signal: 'Content Engagement'
      },
      {
        stage: 2,
        name: 'Email / SMS Capture',
        purpose: 'Convert anonymous traffic into an owned audience.',
        notes: 'User signs up via offer; vehicle data enriches profile.',
        signal: 'Signup Form Submit (generate_lead)'
      },
      {
        stage: 2,
        name: 'Add to Cart',
        purpose: 'Signal purchase intent; power abandonment flows.',
        notes: 'User adds parts to cart; fitment carried as item parameter.',
        signal: 'add_to_cart'
      },
      {
        stage: 2,
        name: 'Installer / Service Quote Request',
        purpose: 'Bridge parts demand to installation services (where offered).',
        notes: 'User requests installation quote or books service appointment.',
        signal: 'Appointment / Quote Request'
      },
      {
        stage: 3,
        name: 'Online Purchase (Ship-to-Home)',
        purpose: 'Core conversion for DIY buyers.',
        notes: 'Order completed; YMM and items captured; server-side mirrored.',
        signal: 'purchase (ship)'
      },
      {
        stage: 3,
        name: 'BOPIS / Counter Pickup Order',
        purpose: 'Capture omnichannel conversions (urgent repairs).',
        notes: 'Pickup order placed; store ID attached.',
        signal: 'purchase (pickup, store_id)'
      },
      {
        stage: 3,
        name: 'Service Booked / Completed (if applicable)',
        purpose: 'Convert lead-gen style service demand.',
        notes: 'Installation or service appointment completed; imported as offline conversion.',
        signal: 'Job Completed (offline import)'
      },
      {
        stage: 4,
        name: 'Delivery & Installation Success',
        purpose: 'Monitor post-purchase experience; reduce returns from fitment errors.',
        notes:
          'Delivery confirmed; install-support content engagement and fitment-return reasons tracked.',
        signal: 'Delivery Confirmed / Return Reason'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate proof with vehicle-specific context.',
        notes: 'Post-install flow requests review with YMM context and photos.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Purchase (Maintenance Cycle)',
        purpose: 'Measure retention across maintenance and project cycles.',
        notes: 'Returning customer buys again; vehicle-based replenishment flows (wear parts).',
        signal: 'purchase (returning customer)'
      },
      {
        stage: 5,
        name: 'Garage / Loyalty Enrollment',
        purpose: 'Deepen retention via saved-vehicle garage and rewards.',
        notes: 'User saves vehicles to garage or joins loyalty program.',
        signal: 'Garage Save / Loyalty Signup'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn enthusiasts into an acquisition channel.',
        notes: 'Customer shares builds/referrals in communities; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Education': {
    title: 'User Journey - Education & Training',
    clientType: 'leadgen',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Reach prospective students at decision moments.',
        notes:
          'User served paid search (program terms), paid social, or YouTube; seasonality-aware campaigns.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on program or location pages; UTMs captured.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Program / Outcomes Content Engagement',
        purpose: 'Gauge program interest and fit.',
        notes: 'User explores curricula, outcomes data, tuition/financing, or location pages.',
        signal: 'Key Page View'
      },
      {
        stage: 2,
        name: 'Info Request Started',
        purpose: 'Capture hand-raisers entering the funnel.',
        notes: 'User starts a request-info form; partial fills tracked.',
        signal: 'form_start'
      },
      {
        stage: 2,
        name: 'Assessment / Placement Tool',
        purpose: 'Capture qualification behavior.',
        notes: 'User takes a readiness quiz or placement assessment.',
        signal: 'Tool Complete'
      },
      {
        stage: 2,
        name: 'Call from Ad / Website',
        purpose: 'Track phone inquiries common in local education.',
        notes: 'User calls from ads or tracked site numbers; call outcomes scored.',
        signal: 'Click-to-Call / DNI Call'
      },
      {
        stage: 2,
        name: 'Open House / Trial Class Registration',
        purpose: 'Progress interest to an experiential step.',
        notes: 'User registers for an open house, campus tour, or free trial session.',
        signal: 'Event Registration'
      },
      {
        stage: 3,
        name: 'Info Request Submitted (Lead)',
        purpose: 'Primary online conversion; entry to admissions pipeline.',
        notes: 'Form submitted; lead created in CRM with attribution for offline import.',
        signal: 'generate_lead / Form Submit'
      },
      {
        stage: 3,
        name: 'Consultation / Interview Completed',
        purpose: 'Progress lead through admissions.',
        notes: 'Admissions consult or assessment completed; stage synced.',
        signal: 'CRM Stage Change (offline import)'
      },
      {
        stage: 3,
        name: 'Application Submitted',
        purpose: 'High-intent conversion preceding enrollment.',
        notes: 'Application completed; application fee if applicable.',
        signal: 'application_submit'
      },
      {
        stage: 3,
        name: 'Enrollment / First Payment',
        purpose: 'Ultimate conversion; ties tuition revenue to media.',
        notes: 'Student enrolls and first payment processes; value imported for bidding.',
        signal: 'Enrollment (offline conversion)'
      },
      {
        stage: 4,
        name: 'Onboarding & First Sessions Attended',
        purpose: 'Drive early engagement that determines persistence.',
        notes: 'Attendance and early-progress milestones tracked from SIS/LMS.',
        signal: 'Attendance / Progress Milestone'
      },
      {
        stage: 4,
        name: 'Progress Reporting & Satisfaction',
        purpose: 'Monitor experience that drives continuation.',
        notes: 'Progress reviews and NPS/parent check-ins tracked.',
        signal: 'Progress Review / NPS'
      },
      {
        stage: 5,
        name: 'Re-Enrollment / Continuation',
        purpose: 'Measure retention across terms.',
        notes: 'Student continues into the next term/level or membership renews.',
        signal: 'Re-Enrollment (CRM)'
      },
      {
        stage: 5,
        name: 'Referral',
        purpose: 'Turn families/students into an acquisition channel.',
        notes: 'Referral submitted (often incentivized); attributed to program.',
        signal: 'Referral Submitted'
      }
    ]
  },
  'JV Travel': {
    title: 'User Journey - Travel & Hospitality',
    clientType: 'ecomm',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Inspire and capture destination demand.',
        notes: 'User served paid social/video inspiration, metasearch, or destination search ads.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, metasearch, or OTA referral.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Destination / Experience Browse',
        purpose: 'Gauge trip interest; build destination-level audiences.',
        notes: 'User browses destinations, properties, itineraries, or experiences.',
        signal: 'view_item_list / Content Engagement'
      },
      {
        stage: 2,
        name: 'Search with Dates / Party Size',
        purpose: 'Capture concrete trip parameters - the key intent signal.',
        notes: 'User runs an availability search; dates, party size, and destination captured.',
        signal: 'search (dates, party)'
      },
      {
        stage: 2,
        name: 'Listing / Room / Package View',
        purpose: 'Identify specific inventory of interest.',
        notes: 'User views specific properties, rooms, or packages; price-checked items tracked.',
        signal: 'view_item'
      },
      {
        stage: 2,
        name: 'Email Capture / Price Alert',
        purpose: 'Convert researchers into an addressable audience across long booking windows.',
        notes: 'User saves a trip, sets a price alert, or subscribes to deals.',
        signal: 'Signup / Alert Set (generate_lead)'
      },
      {
        stage: 2,
        name: 'Booking Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User begins checkout; traveler details step tracked.',
        signal: 'begin_checkout'
      },
      {
        stage: 3,
        name: 'Booking Confirmed',
        purpose: 'Core conversion; feeds ROAS and value-based bidding.',
        notes:
          'Reservation completed; booking value, dates, and lead time captured; server-side mirrored.',
        signal: 'purchase (booking)'
      },
      {
        stage: 3,
        name: 'Ancillary / Upgrade Purchase',
        purpose: 'Grow booking value pre-trip.',
        notes: 'User adds insurance, transfers, excursions, or room upgrades.',
        signal: 'purchase (ancillary)'
      },
      {
        stage: 4,
        name: 'Pre-Trip Engagement',
        purpose: 'Reduce cancellations; prepare travelers.',
        notes:
          'Confirmation/pre-arrival flows engaged; app downloads, itinerary views, check-in completed.',
        signal: 'Pre-Arrival Engagement / Check-In'
      },
      {
        stage: 4,
        name: 'Trip Taken / Stay Completed',
        purpose: 'Confirm fulfilled revenue; trigger post-stay flows.',
        notes: 'Stay/trip completed status synced from PMS/booking system.',
        signal: 'Stay Completed (PMS)'
      },
      {
        stage: 4,
        name: 'Review Request',
        purpose: 'Generate reviews that drive future conversion.',
        notes: 'Post-stay flow requests reviews on-site and on key travel platforms.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Rebooking',
        purpose: 'Measure repeat travel behavior.',
        notes: 'Guest books a future trip; repeat-guest rate and booking-window cohorts.',
        signal: 'purchase (returning guest)'
      },
      {
        stage: 5,
        name: 'Loyalty Program Enrollment & Activity',
        purpose: 'Deepen retention and direct-booking preference.',
        notes: 'Guest joins loyalty program; points and tier activity tracked.',
        signal: 'Loyalty Signup / Activity'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn guests into an acquisition channel.',
        notes: 'Guest shares referral or UGC; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Media': {
    title: 'User Journey - Media, Entertainment & Hobbies',
    clientType: 'ecomm_sub',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose: 'Establish first touch; content-led creative.',
        notes:
          'User served paid social/video featuring content samples, trailers, or hobby projects.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website / App Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site or app store page; UTMs and install referrers captured.',
        signal: 'Session Start / Store Page View'
      },
      {
        stage: 1,
        name: 'Content Sampling',
        purpose: 'Let the product sell itself; build engaged audiences.',
        notes: 'User samples free content (articles, previews, freemium play, project ideas).',
        signal: 'Content Engagement'
      },
      {
        stage: 2,
        name: 'Registration (Free Account)',
        purpose: 'Convert anonymous users into known users.',
        notes: 'User creates a free account or installs the app; user_id set.',
        signal: 'sign_up / first_open'
      },
      {
        stage: 2,
        name: 'Email Capture / Wishlist',
        purpose: 'Build addressable audience and demand signals.',
        notes: 'User subscribes to newsletters or wishlists products/titles.',
        signal: 'Signup / add_to_wishlist'
      },
      {
        stage: 2,
        name: 'Paywall / Offer Encounter',
        purpose: 'Identify monetization-ready users.',
        notes: 'User hits metered paywall, trial offer, or premium prompt; offer views tracked.',
        signal: 'paywall_view / offer_view'
      },
      {
        stage: 2,
        name: 'Checkout / Trial Started',
        purpose: 'Identify bottom-funnel users; trigger abandonment recovery.',
        notes: 'User begins subscription checkout or starts free trial.',
        signal: 'begin_checkout / trial_start'
      },
      {
        stage: 3,
        name: 'Purchase Completed (One-Time)',
        purpose: 'Convert transactional buyers (merchandise, tickets, single titles, kits).',
        notes: 'One-time order completed; items and revenue captured.',
        signal: 'purchase (one-time)'
      },
      {
        stage: 3,
        name: 'Subscription Started',
        purpose: 'Core recurring conversion.',
        notes:
          'Paid subscription begins (or trial converts); plan and billing period captured; server-side mirrored.',
        signal: 'subscription_start'
      },
      {
        stage: 4,
        name: 'Onboarding & First Value Moment',
        purpose: 'Drive habit formation that determines retention.',
        notes:
          'User completes onboarding and reaches first value (first read/watch/play/project completed).',
        signal: 'Activation Event'
      },
      {
        stage: 4,
        name: 'Engagement Depth',
        purpose: 'Predict churn via consumption behavior.',
        notes: 'Sessions, content consumed, and streak/frequency metrics monitored.',
        signal: 'Engagement Score'
      },
      {
        stage: 5,
        name: 'Renewal (Rebill)',
        purpose: 'Measure retained subscription revenue.',
        notes: 'Subscription renews; cohort retention curves tracked.',
        signal: 'recurring_purchase'
      },
      {
        stage: 5,
        name: 'Cancellation & Winback',
        purpose: 'Quantify churn; recover lapsed subscribers.',
        notes: 'Cancel with reason capture; pause offers and winback flows tracked.',
        signal: 'subscription_cancel / reactivation'
      },
      {
        stage: 5,
        name: 'Upsell / Cross-Sell',
        purpose: 'Grow ARPU (annual plans, bundles, merchandise, add-ons).',
        notes: 'Subscriber upgrades plan or buys adjacent products.',
        signal: 'Plan Upgrade / Add-On Purchase'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn fans into an acquisition channel.',
        notes: 'User shares referral or gift subscriptions; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Furniture': {
    title: 'User Journey - Furniture & Home Goods',
    clientType: 'ecomm_store',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure (National + Local)',
        purpose: 'Establish first touch across DTC and showroom demand.',
        notes:
          'User served inspiration-led social creative, search, and geo-targeted local campaigns.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes: 'User lands on site from paid, organic, or local listings.',
        signal: 'Session Start (source/medium)'
      },
      {
        stage: 1,
        name: 'Showroom Locator View',
        purpose: 'Identify omnichannel intent early.',
        notes: 'User views the showroom locator or a store detail page; store ID captured.',
        signal: 'store_locator_view'
      },
      {
        stage: 2,
        name: 'Product Detail View w/ Visualizer',
        purpose: 'Connect interest to space fit - the categorys key barrier.',
        notes:
          'User views PDPs, uses AR/room visualizer, orders fabric swatches; local availability checked.',
        signal: 'view_item / visualizer_use / swatch_order'
      },
      {
        stage: 2,
        name: 'Financing Pre-Qualification',
        purpose: 'Remove the price barrier on high-AOV purchases.',
        notes: 'User checks financing options or pre-qualifies (soft pull).',
        signal: 'financing_prequal'
      },
      {
        stage: 2,
        name: 'Design Consultation Booked',
        purpose: 'Bridge digital interest to high-converting assisted selling.',
        notes: 'User books an in-store or virtual design consultation; booking synced.',
        signal: 'Appointment Booked'
      },
      {
        stage: 2,
        name: 'Add to Cart',
        purpose: 'Signal online purchase intent.',
        notes: 'User adds items to cart; delivery estimates surfaced.',
        signal: 'add_to_cart'
      },
      {
        stage: 3,
        name: 'Online Purchase (Delivery)',
        purpose: 'Core DTC conversion.',
        notes:
          'Order completed for delivery; transaction and financing method captured; server-side mirrored.',
        signal: 'purchase (delivery)'
      },
      {
        stage: 3,
        name: 'In-Store Purchase (Matched)',
        purpose: 'Close the loop on showroom revenue driven by digital media.',
        notes:
          'POS transaction matched to digital identity via consult booking, financing app, or loyalty; imported as offline conversion; store visits monitored.',
        signal: 'Offline Conversion Import / Store Visit'
      },
      {
        stage: 4,
        name: 'Scheduled Delivery & White-Glove Setup',
        purpose: 'Monitor the long-lead fulfillment experience.',
        notes: 'Delivery scheduling, updates, and setup completion tracked from OMS.',
        signal: 'Delivery Scheduled / Completed'
      },
      {
        stage: 4,
        name: 'Review / UGC Request',
        purpose: 'Generate social proof with room photos.',
        notes: 'Post-delivery flow requests reviews and styled-room photos.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Room / Category Expansion Purchase',
        purpose: 'Measure retention across rooms and life events.',
        notes: 'Returning customer furnishes additional rooms; category-expansion flows.',
        signal: 'purchase (returning customer)'
      },
      {
        stage: 5,
        name: 'Loyalty / Trade Program Enrollment',
        purpose: 'Deepen retention; capture designer/trade demand.',
        notes: 'Customer joins loyalty or trade/designer program.',
        signal: 'Loyalty / Trade Signup'
      },
      {
        stage: 5,
        name: 'Referral / Advocacy',
        purpose: 'Turn customers into an acquisition channel.',
        notes: 'Customer refers friends; redemptions attributed.',
        signal: 'Referral Redemption'
      }
    ]
  },
  'JV Marketplace': {
    title: 'User Journey - Marketplaces, Resale & Delivery',
    clientType: 'ecomm',
    milestones: [
      {
        stage: 1,
        name: 'Paid Media Exposure',
        purpose:
          'Acquire both sides of the marketplace (buyers; sellers/consignors where applicable).',
        notes:
          'User served paid social/search/app campaigns; buyer vs. seller intent split at campaign level.',
        signal: 'Ad Impression / Ad Click'
      },
      {
        stage: 1,
        name: 'Website / App Visit',
        purpose: 'Initial user identification; understand source of traffic.',
        notes:
          'User lands on site or installs app; install referrer and deferred deep links captured.',
        signal: 'Session Start / first_open'
      },
      {
        stage: 1,
        name: 'Browse / Search',
        purpose: 'Gauge category interest; build intent audiences.',
        notes: 'User searches or browses listings; query and category data captured.',
        signal: 'search / view_item_list'
      },
      {
        stage: 2,
        name: 'Account Created',
        purpose: 'Convert anonymous users into known users - prerequisite to transact.',
        notes: 'User registers; user_id set for cross-device and app/web stitching.',
        signal: 'sign_up'
      },
      {
        stage: 2,
        name: 'Saved Search / Favorite / Alert',
        purpose: 'Capture demand signals across long consideration windows.',
        notes:
          'User saves searches, favorites listings, or sets alerts (price drop, back-in-stock).',
        signal: 'add_to_wishlist / alert_set'
      },
      {
        stage: 2,
        name: 'Add to Cart / Offer Made',
        purpose: 'Signal transaction intent (fixed price or negotiated).',
        notes: 'User adds to cart, bids, or makes an offer; offer flows tracked.',
        signal: 'add_to_cart / offer_made'
      },
      {
        stage: 2,
        name: 'Seller Listing Started (supply side)',
        purpose: 'Capture supply-side activation where the model requires it.',
        notes: 'User begins a listing or consignment intake flow.',
        signal: 'listing_start'
      },
      {
        stage: 3,
        name: 'First Purchase / Order Placed',
        purpose: 'Core buyer conversion.',
        notes:
          'First order completed; GMV, take-rate-relevant fields captured; server-side mirrored.',
        signal: 'purchase (first)'
      },
      {
        stage: 3,
        name: 'First Sale Completed (supply side)',
        purpose: 'Core seller conversion for two-sided health.',
        notes: 'Sellers first item sells or consignment is accepted and listed.',
        signal: 'first_sale / consignment_accepted'
      },
      {
        stage: 4,
        name: 'Fulfillment & Delivery Experience',
        purpose: 'Monitor the trust-critical fulfillment loop.',
        notes:
          'Shipping/delivery or courier tracking engagement; authentication steps (resale) tracked.',
        signal: 'Delivery Confirmed / Authentication Complete'
      },
      {
        stage: 4,
        name: 'Rating / Review Exchange',
        purpose: 'Maintain marketplace trust signals.',
        notes: 'Buyer and seller ratings submitted post-transaction.',
        signal: 'Review Submitted'
      },
      {
        stage: 5,
        name: 'Repeat Purchase Frequency',
        purpose: 'Measure buyer retention - the core marketplace KPI.',
        notes: 'Order frequency and category expansion tracked; power-buyer segments built.',
        signal: 'purchase (returning)'
      },
      {
        stage: 5,
        name: 'Membership / Program Enrollment',
        purpose: 'Deepen retention via paid membership or free-delivery programs.',
        notes: 'User joins membership (e.g., free delivery, insider programs); usage tracked.',
        signal: 'Membership Signup'
      },
      {
        stage: 5,
        name: 'Referral / Invite',
        purpose: 'Leverage network effects for acquisition.',
        notes: 'User invites friends; dual-sided incentives attributed.',
        signal: 'Referral Redemption'
      }
    ]
  }
};

export const LIBRARY: LibraryMilestone[] = [
  {
    section: 'App & Mobile',
    stage: 1,
    name: 'App Store Page View',
    purpose: 'Measure app-acquisition funnel entry.',
    notes:
      'User views the App Store / Play Store listing from web prompts or app campaigns; store analytics + MMP capture source.',
    signal: 'Store Page View (MMP)',
    when: 'Clients with a native app central to the experience (marketplaces, delivery, media, finance).'
  },
  {
    section: 'App & Mobile',
    stage: 2,
    name: 'App Install & First Open',
    purpose: 'Convert web/paid demand into an owned app relationship.',
    notes:
      'Install attributed via MMP (AppsFlyer/Adjust/Branch); deferred deep link carries the user to intended content.',
    signal: 'first_open (MMP attributed)',
    when: 'App-first clients; also eComm brands pushing app for retention.'
  },
  {
    section: 'App & Mobile',
    stage: 2,
    name: 'Push / Notification Opt-In',
    purpose: 'Unlock the highest-frequency owned channel.',
    notes: 'User grants push permission; opt-in rate tracked by prompt placement and timing.',
    signal: 'push_opt_in',
    when: 'Any app client; critical for delivery, media, and gaming.'
  },
  {
    section: 'App & Mobile',
    stage: 3,
    name: 'First In-App Purchase / Order',
    purpose: 'Distinguish app-channel conversions for budget and CAC math.',
    notes: 'First transaction completed in-app; app vs. web revenue split maintained.',
    signal: 'in_app_purchase',
    when: 'App clients monetizing directly in-app.'
  },
  {
    section: 'App & Mobile',
    stage: 2,
    name: 'Web-to-App Handoff',
    purpose: 'Measure cross-surface continuity.',
    notes:
      'User taps a smart banner or QR to continue a web session in-app; session stitching via user_id.',
    signal: 'deep_link_open',
    when: 'Clients where checkout or engagement is materially better in-app.'
  },
  {
    section: 'Waitlist, Pre-Order & Drops',
    stage: 2,
    name: 'Waitlist / Back-in-Stock Signup',
    purpose: 'Capture demand for out-of-stock or unreleased products.',
    notes:
      'User joins a waitlist or requests restock alerts; item-level demand data informs buying.',
    signal: 'waitlist_join / bis_signup',
    when: 'Drop-model fashion, sneakers, collectibles, supply-constrained hardware.'
  },
  {
    section: 'Waitlist, Pre-Order & Drops',
    stage: 3,
    name: 'Pre-Order Placed',
    purpose: 'Convert demand ahead of availability; forecast inventory.',
    notes:
      'User completes a pre-order with deposit or full payment; expected-ship-date comms tracked.',
    signal: 'purchase (pre_order)',
    when: 'New product launches, hardware, publishing, limited runs.'
  },
  {
    section: 'Waitlist, Pre-Order & Drops',
    stage: 2,
    name: 'Drop / Launch Reminder Set',
    purpose: 'Build hype-cycle audiences for launch moments.',
    notes:
      'User sets a reminder or subscribes to a specific drop; notification engagement tracked.',
    signal: 'reminder_set',
    when: 'Streetwear, collectibles (Whatnot-style), limited editions.'
  },
  {
    section: 'Waitlist, Pre-Order & Drops',
    stage: 3,
    name: 'Raffle Entry / Purchase Access Granted',
    purpose: 'Manage over-subscribed demand fairly; capture entrant audiences.',
    notes: 'User enters a purchase raffle; winners/losers segmented for follow-up offers.',
    signal: 'raffle_entry',
    when: 'Hyped sneaker/collectible drops.'
  },
  {
    section: 'Financing, Payments & Benefits',
    stage: 2,
    name: 'BNPL Selection at Checkout',
    purpose: 'Remove price friction; measure payment-mix impact on conversion.',
    notes:
      'User selects Affirm/Klarna/Afterpay; approval and completion rates tracked by AOV band.',
    signal: 'payment_method_select (BNPL)',
    when: 'High-AOV eComm (furniture, sporting goods, jewelry, mattresses).'
  },
  {
    section: 'Financing, Payments & Benefits',
    stage: 2,
    name: 'Financing Pre-Qualification (Soft Pull)',
    purpose: 'Qualify big-ticket buyers early without credit impact.',
    notes: 'User pre-qualifies for store financing; approval amount available for personalization.',
    signal: 'financing_prequal',
    when: 'Furniture, home improvement, auto, medical/dental, jewelry.'
  },
  {
    section: 'Financing, Payments & Benefits',
    stage: 3,
    name: 'HSA / FSA Payment Used',
    purpose: 'Capture benefit-eligible conversions and messaging lift.',
    notes:
      'User pays with HSA/FSA (e.g., via Truemed or Sika); eligibility messaging engagement tracked upstream.',
    signal: 'purchase (hsa_fsa)',
    when: 'Health, wellness, fitness equipment, eyewear, select supplements.'
  },
  {
    section: 'Financing, Payments & Benefits',
    stage: 3,
    name: 'Gift Card Purchase / Redemption',
    purpose: 'Track stored-value acquisition and its second-order conversions.',
    notes: 'Gift card bought or redeemed; redemption ties gifter to recipient acquisition.',
    signal: 'gift_card_purchase / redemption',
    when: 'Retail, dining, experiences, marketplaces.'
  },
  {
    section: 'Financing, Payments & Benefits',
    stage: 2,
    name: 'Insurance / Benefit Eligibility Check',
    purpose: 'Qualify users whose payer covers the product/service.',
    notes:
      'User runs an insurance-coverage or employer-benefit check; eligible vs. cash-pay segmented.',
    signal: 'eligibility_check',
    when: 'Telehealth, mental health (Headway/Talkiatry-type), hearing, DME.'
  },
  {
    section: 'Regulated Health & Telehealth',
    stage: 2,
    name: 'Intake / Medical Questionnaire Completed',
    purpose: 'Qualify patients and personalize care pathways (privacy-safe).',
    notes:
      'User completes clinical intake; only non-PHI marketing signals leave the platform; consent captured.',
    signal: 'intake_complete (privacy-safe)',
    when: 'Telehealth, Rx wellness (Hims/Ro-type), mental health, weight care.'
  },
  {
    section: 'Regulated Health & Telehealth',
    stage: 3,
    name: 'Provider Consultation Completed',
    purpose: 'Core conversion step preceding prescription/treatment.',
    notes:
      'Synchronous or async visit completed; completion imported as a conversion without diagnosis data.',
    signal: 'consult_complete (offline import)',
    when: 'Telehealth and clinic-based services.'
  },
  {
    section: 'Regulated Health & Telehealth',
    stage: 3,
    name: 'Prescription Approved & First Fill',
    purpose: 'True activation for Rx models.',
    notes:
      'Provider approves treatment; first fulfillment ships; marketed as generic conversion event only.',
    signal: 'rx_first_fill (privacy-safe)',
    when: 'Rx telehealth, compounded meds, dermatology programs.'
  },
  {
    section: 'Regulated Health & Telehealth',
    stage: 2,
    name: 'Appointment Scheduled (Clinic)',
    purpose: 'Bridge digital demand to in-person care.',
    notes: 'User books at a physical location; show-rate tracked by source.',
    signal: 'appointment_booked',
    when: 'Med spas, dental, vision, hearing, physical therapy, vet clinics.'
  },
  {
    section: 'Regulated Health & Telehealth',
    stage: 4,
    name: 'Refill / Treatment Adherence',
    purpose: 'Predict churn in recurring-care models.',
    notes: 'Refill timing and check-in completion monitored; care-team outreach on lapse.',
    signal: 'refill_on_time',
    when: 'Rx subscription and chronic-care programs.'
  },
  {
    section: 'Age-Gated & Compliance-Heavy',
    stage: 1,
    name: 'Age Gate Passed',
    purpose: 'Meet regulatory requirements; filter qualified audiences.',
    notes: 'User self-certifies or verifies age on entry; failure paths tracked in aggregate.',
    signal: 'age_gate_pass',
    when: 'Alcohol, tobacco/vape alternatives, CBD, gambling, dating.'
  },
  {
    section: 'Age-Gated & Compliance-Heavy',
    stage: 2,
    name: 'KYC / Identity Verification Completed',
    purpose: 'Regulatory prerequisite to transact; major funnel drop-off point.',
    notes:
      'User completes identity verification (doc upload / data match); step-level abandonment tracked.',
    signal: 'kyc_complete',
    when: 'Fintech, gambling/prediction markets (Kalshi/Fliff-type), crypto, resale of luxury.'
  },
  {
    section: 'Age-Gated & Compliance-Heavy',
    stage: 3,
    name: 'First Deposit Funded',
    purpose: 'Core activation conversion for wallet-based models.',
    notes: 'User funds account for the first time; deposit value imported for value-based bidding.',
    signal: 'first_deposit',
    when: 'Sportsbooks, DFS, prediction markets, trading apps.'
  },
  {
    section: 'Age-Gated & Compliance-Heavy',
    stage: 3,
    name: 'First Wager / Trade Placed',
    purpose: 'True product activation beyond funding.',
    notes: 'First bet/entry/trade placed; time-to-first-action tracked from registration.',
    signal: 'first_bet / first_trade',
    when: 'Gambling, DFS, trading platforms.'
  },
  {
    section: 'Age-Gated & Compliance-Heavy',
    stage: 4,
    name: 'Responsible Play / Limit Setting',
    purpose: 'Compliance and retention-health signal.',
    notes:
      'User sets deposit/time limits; engagement with responsible-gaming tools monitored in aggregate.',
    signal: 'limit_set',
    when: 'Gambling and gaming clients (required in many jurisdictions).'
  },
  {
    section: 'B2B Advanced',
    stage: 1,
    name: 'Target Account Engagement (ABM)',
    purpose: 'Measure account-level penetration before any form fill.',
    notes:
      'Matched target-account visits and content engagement scored via ABM platform (6sense/Demandbase-type).',
    signal: 'Account Engagement Score',
    when: 'Enterprise B2B with named-account motions.'
  },
  {
    section: 'B2B Advanced',
    stage: 2,
    name: 'Interactive Demo / Product Tour',
    purpose: 'Qualify intent without sales friction.',
    notes:
      'User completes a self-guided product tour (Navattic/Storylane-type); completion depth scored.',
    signal: 'tour_complete',
    when: 'PLG and sales-assisted SaaS.'
  },
  {
    section: 'B2B Advanced',
    stage: 2,
    name: 'ROI / Pricing Calculator Completed',
    purpose: 'Capture business-case building - a late-stage buying signal.',
    notes: 'User completes an ROI calculator; inputs (privacy-safe) inform lead scoring.',
    signal: 'calculator_complete',
    when: 'Mid-market/enterprise SaaS and services.'
  },
  {
    section: 'B2B Advanced',
    stage: 3,
    name: 'Security / Procurement Review Started',
    purpose: 'Track the deal-velocity chokepoint in enterprise sales.',
    notes:
      'Security questionnaire, SOC 2 request, or vendor-onboarding initiated; stage synced from CRM.',
    signal: 'CRM Stage (security_review)',
    when: 'Enterprise deals with procurement cycles.'
  },
  {
    section: 'B2B Advanced',
    stage: 3,
    name: 'Multi-Stakeholder Engagement',
    purpose: 'Measure buying-committee coverage.',
    notes: '2+ contacts from the same account engaged; contact-role coverage tracked in CRM/MAP.',
    signal: 'Account Contact Count',
    when: 'Enterprise B2B; informs ABM plays.'
  },
  {
    section: 'B2B Advanced',
    stage: 2,
    name: 'RFP / Proposal Requested',
    purpose: 'Capture formal evaluation entry.',
    notes:
      'Prospect requests RFP response or formal proposal; tracked as high-value pipeline signal.',
    signal: 'rfp_request',
    when: 'Agencies, services firms, enterprise vendors.'
  },
  {
    section: 'Resale, Trade-In & Sustainability',
    stage: 2,
    name: 'Trade-In Quote Requested',
    purpose: 'Acquire supply and subsidize new purchases.',
    notes: 'User gets an instant trade-in value for used items; quote-to-ship conversion tracked.',
    signal: 'trade_in_quote',
    when: 'Resale (TheRealReal/StockX-type), electronics, sporting goods, fashion take-back.'
  },
  {
    section: 'Resale, Trade-In & Sustainability',
    stage: 3,
    name: 'Consignment / Sell Submission Accepted',
    purpose: 'Core supply-side conversion for resale models.',
    notes: 'Sellers items accepted after authentication/inspection; payout method captured.',
    signal: 'consignment_accepted',
    when: 'Luxury resale, consignment marketplaces.'
  },
  {
    section: 'Resale, Trade-In & Sustainability',
    stage: 3,
    name: 'Trade-In Credit Applied to Purchase',
    purpose: 'Measure the trade-in-to-new-sale loop.',
    notes: 'Trade-in credit redeemed against a new purchase; incremental AOV tracked.',
    signal: 'purchase (trade_in_credit)',
    when: 'Brands with take-back programs.'
  },
  {
    section: 'Resale, Trade-In & Sustainability',
    stage: 4,
    name: 'Authentication Completed',
    purpose: 'Trust-critical fulfillment step in luxury resale.',
    notes: 'Item passes authentication; time-in-authentication monitored for experience.',
    signal: 'authentication_complete',
    when: 'Luxury and collectibles resale.'
  },
  {
    section: 'Resale, Trade-In & Sustainability',
    stage: 5,
    name: 'Repair / Refurbish Service Used',
    purpose: 'Extend product life; retention touchpoint for premium brands.',
    notes: 'User books repair or refurbishment; NPS post-service.',
    signal: 'repair_booked',
    when: 'Premium outdoor, footwear, luggage, watches.'
  },
  {
    section: 'Gifting & Registry',
    stage: 2,
    name: 'Gift Finder / Quiz Used',
    purpose: 'Convert low-context gift shoppers.',
    notes: 'User completes gift-finder flow; recipient attributes captured for recommendations.',
    signal: 'gift_quiz_complete',
    when: 'Seasonal-heavy retail (flowers, jewelry, toys, candy).'
  },
  {
    section: 'Gifting & Registry',
    stage: 3,
    name: 'Gift Purchase (Ship to Recipient)',
    purpose: 'Distinguish gifter conversions; unlock recipient acquisition.',
    notes: 'Order flagged as gift with message/wrap; recipient address distinct from buyer.',
    signal: 'purchase (gift_flag)',
    when: '1-800-Flowers-type, jewelry, food gifting.'
  },
  {
    section: 'Gifting & Registry',
    stage: 2,
    name: 'Registry Created (Wedding / Baby)',
    purpose: 'Capture the life-stage anchor conversion.',
    notes: 'User creates a registry; expected event date drives lifecycle flows.',
    signal: 'registry_create',
    when: 'Baby, home goods, department-style retail.'
  },
  {
    section: 'Gifting & Registry',
    stage: 3,
    name: 'Gift Card / e-Gift Sent',
    purpose: 'Instant-gifting conversion path.',
    notes: 'e-Gift delivered to recipient; open and redemption tracked.',
    signal: 'egift_send / redemption',
    when: 'All gifting-relevant retail.'
  },
  {
    section: 'Gifting & Registry',
    stage: 5,
    name: 'Recipient Converted to Customer',
    purpose: 'Measure gifting as an acquisition channel.',
    notes: 'Gift recipient makes their own first purchase; attributed to original gifter cohort.',
    signal: 'purchase (recipient_first)',
    when: 'Gifting-heavy brands with recipient capture.'
  },
  {
    section: 'Subscription Advanced',
    stage: 3,
    name: 'Gift Subscription Purchased',
    purpose: 'Seasonal acquisition path with distinct economics.',
    notes: 'Gifter buys a fixed-term subscription; recipient onboarding tracked separately.',
    signal: 'purchase (gift_sub)',
    when: 'Meal kits, media, book/wine clubs.'
  },
  {
    section: 'Subscription Advanced',
    stage: 4,
    name: 'Failed Payment Recovered (Dunning)',
    purpose: 'Protect revenue from involuntary churn - often 20-40% of churn.',
    notes: 'Card failure triggers dunning flow; recovery rate by attempt tracked.',
    signal: 'payment_recovered',
    when: 'All subscription clients.'
  },
  {
    section: 'Subscription Advanced',
    stage: 4,
    name: 'Plan Pause (Save Offer Accepted)',
    purpose: 'Convert would-be cancels into retained subscribers.',
    notes: 'Cancel-intent user accepts pause/discount/swap save offer; save rate by offer tracked.',
    signal: 'save_offer_accepted',
    when: 'All subscription clients with cancel flows.'
  },
  {
    section: 'Subscription Advanced',
    stage: 5,
    name: 'Annual / Prepaid Upgrade',
    purpose: 'Lock in retention and improve cash flow.',
    notes: 'Monthly subscriber converts to annual/prepaid plan.',
    signal: 'plan_upgrade (annual)',
    when: 'Media, SaaS-like consumer subs, supplements.'
  },
  {
    section: 'Subscription Advanced',
    stage: 5,
    name: 'Subscription Anniversary / Milestone',
    purpose: 'Celebrate tenure; trigger loyalty rewards.',
    notes: 'Subscriber hits 6/12-month milestones; milestone-flow engagement tracked.',
    signal: 'milestone_reached',
    when: 'Retention-mature subscription programs.'
  },
  {
    section: 'Community, Influencer & Social Commerce',
    stage: 2,
    name: 'Ambassador / Creator Application',
    purpose: 'Build owned influencer supply.',
    notes: 'User applies to ambassador program; acceptance and activation tracked.',
    signal: 'ambassador_apply',
    when: 'Fashion, fitness, beauty, outdoor with community DNA.'
  },
  {
    section: 'Community, Influencer & Social Commerce',
    stage: 2,
    name: 'Live Shopping Event Attended',
    purpose: 'Measure live-commerce engagement.',
    notes:
      'User joins a livestream shopping event (on-site, Whatnot, TikTok Shop); watch time and interactions tracked.',
    signal: 'live_event_join',
    when: 'Collectibles, beauty, apparel with live commerce.'
  },
  {
    section: 'Community, Influencer & Social Commerce',
    stage: 3,
    name: 'Social Checkout (TikTok Shop / IG)',
    purpose: 'Capture off-site conversions in social storefronts.',
    notes:
      'Purchase completes inside a social platform shop; reconciled with feed and creator attribution.',
    signal: 'social_shop_purchase',
    when: 'Brands active on TikTok Shop / Meta Shops.'
  },
  {
    section: 'Community, Influencer & Social Commerce',
    stage: 2,
    name: 'UGC Submission / Contest Entry',
    purpose: 'Generate content supply and engagement.',
    notes: 'User submits photos/videos to a campaign; rights-management consent captured.',
    signal: 'ugc_submit',
    when: 'Community-led brands.'
  },
  {
    section: 'Community, Influencer & Social Commerce',
    stage: 2,
    name: 'Community / Forum Registration',
    purpose: 'Deepen engagement in enthusiast categories.',
    notes: 'User joins brand community, Discord, or forum; activity feeds retention scoring.',
    signal: 'community_join',
    when: 'Gaming, hobbies, fitness, trading.'
  },
  {
    section: 'Events & Experiential',
    stage: 2,
    name: 'Webinar / Class Registration',
    purpose: 'Capture mid-funnel hand-raisers.',
    notes:
      'User registers for a webinar, masterclass, or virtual class; attendance tracked separately from registration.',
    signal: 'event_register',
    when: 'B2B, education, cooking/craft brands, fitness.'
  },
  {
    section: 'Events & Experiential',
    stage: 2,
    name: 'In-Store Event / Workshop RSVP',
    purpose: 'Drive store traffic with experiences.',
    notes:
      'User RSVPs to a store event (fitting day, demo night, workshop); check-in ties digital identity to visit.',
    signal: 'event_rsvp / check_in',
    when: 'Retail with showrooms, run clubs, craft retailers.'
  },
  {
    section: 'Events & Experiential',
    stage: 3,
    name: 'Ticket Purchased',
    purpose: 'Core conversion for experience-led businesses.',
    notes: 'Event ticket purchased; seat/tier value captured.',
    signal: 'purchase (ticket)',
    when: 'Entertainment, sports, attractions, dining events.'
  },
  {
    section: 'Events & Experiential',
    stage: 2,
    name: 'Pop-Up / Trunk Show Visit',
    purpose: 'Measure experiential marketing impact.',
    notes:
      'Visit captured via QR check-in, WiFi, or geofenced audience; post-visit conversion window measured.',
    signal: 'popup_checkin',
    when: 'DTC brands testing physical retail.'
  },
  {
    section: 'Loyalty & Membership Advanced',
    stage: 3,
    name: 'Paid Membership Purchased',
    purpose: 'Convert to fee-based loyalty with structurally higher LTV.',
    notes: 'User buys paid membership (free shipping / insider club); breakage and usage tracked.',
    signal: 'membership_purchase',
    when: 'Marketplaces, high-frequency retail (Instacart+-type models).'
  },
  {
    section: 'Loyalty & Membership Advanced',
    stage: 5,
    name: 'Points Redemption',
    purpose: 'Measure loyalty-program health via redemption activity.',
    notes: 'Member redeems points; redemption-active members segmented (highest retention cohort).',
    signal: 'points_redeem',
    when: 'All points-based programs.'
  },
  {
    section: 'Loyalty & Membership Advanced',
    stage: 5,
    name: 'Tier Upgrade / VIP Status',
    purpose: 'Identify and protect highest-value customers.',
    notes: 'Member crosses tier threshold; VIP treatment flows triggered.',
    signal: 'tier_upgrade',
    when: 'Beauty, fashion, travel loyalty programs.'
  },
  {
    section: 'Loyalty & Membership Advanced',
    stage: 5,
    name: 'Loyalty Card Linked (In-Store)',
    purpose: 'Stitch POS behavior to the digital profile.',
    notes: 'Member links card/phone at POS; in-store baskets join the online profile.',
    signal: 'card_link / pos_match',
    when: 'Omnichannel retail.'
  },
  {
    section: 'Warranty, Service & Ownership',
    stage: 4,
    name: 'Product Registration',
    purpose: 'Build first-party ownership data; enable recall contactability.',
    notes:
      'User registers product post-purchase (serial, purchase date); registration rate by channel tracked.',
    signal: 'product_registration',
    when: 'Durables: baby gear (safety-critical), electronics, appliances, power equipment.'
  },
  {
    section: 'Warranty, Service & Ownership',
    stage: 4,
    name: 'Extended Warranty / Protection Attach',
    purpose: 'Grow post-purchase revenue and retention touchpoints.',
    notes:
      'User buys protection plan at or after checkout (Extend/Clyde-type); attach rate by category.',
    signal: 'warranty_purchase',
    when: 'Electronics, furniture, sporting goods, appliances.'
  },
  {
    section: 'Warranty, Service & Ownership',
    stage: 4,
    name: 'Support Ticket / Repair Request',
    purpose: 'Monitor ownership experience that drives repeat rates.',
    notes: 'User opens support case or books repair; resolution time and CSAT tracked.',
    signal: 'support_ticket',
    when: 'Durables and technical products.'
  },
  {
    section: 'Warranty, Service & Ownership',
    stage: 5,
    name: 'Replacement Part / Accessory Purchase',
    purpose: 'Capture the ownership-lifecycle revenue stream.',
    notes: 'Owner buys parts/accessories/consumables for a registered product.',
    signal: 'purchase (accessory)',
    when: 'Equipment, appliances, auto, coffee (pods), printers.'
  },
  {
    section: 'Wholesale, Trade & Dealer',
    stage: 2,
    name: 'Trade / Pro Program Application',
    purpose: 'Capture professional buyers with distinct economics.',
    notes:
      'Designer, contractor, or pro applies for trade pricing; verification and approval tracked.',
    signal: 'trade_apply',
    when: 'Furniture, building products, beauty (pro), tools.'
  },
  {
    section: 'Wholesale, Trade & Dealer',
    stage: 2,
    name: 'Dealer / Retailer Locator Click',
    purpose: 'Route demand to wholesale channels without losing measurement.',
    notes: 'User finds an authorized dealer; clicks passed to co-op reporting where available.',
    signal: 'dealer_locator_click',
    when: 'Brands with dealer networks (sporting goods, power equipment, mattresses).'
  },
  {
    section: 'Wholesale, Trade & Dealer',
    stage: 3,
    name: 'Wholesale / B2B Order Placed',
    purpose: 'Track the B2B side of hybrid brands.',
    notes: 'Approved trade account places order on B2B portal; separate from DTC reporting.',
    signal: 'purchase (wholesale)',
    when: 'Hybrid DTC/wholesale brands.'
  },
  {
    section: 'Wholesale, Trade & Dealer',
    stage: 2,
    name: 'Sample / Swatch Order',
    purpose: 'Qualify high-consideration buyers with physical evaluation.',
    notes:
      'User orders samples/swatches (fabric, flooring, paint); sample-to-order conversion tracked.',
    signal: 'sample_order',
    when: 'Furniture, textiles, building materials, wallpaper.'
  },
  {
    section: 'Nonprofit & Cause',
    stage: 3,
    name: 'One-Time Donation',
    purpose: 'Core conversion for cause organizations.',
    notes: 'Donor completes gift; amount, fund designation, and source captured.',
    signal: 'donation',
    when: 'Nonprofits (Save the Children/JED-type accounts).'
  },
  {
    section: 'Nonprofit & Cause',
    stage: 3,
    name: 'Recurring Giving Enrollment',
    purpose: 'The subscription-equivalent conversion with dominant LTV.',
    notes: 'Donor starts monthly giving; sustainer cohort retention tracked.',
    signal: 'recurring_donation_start',
    when: 'All nonprofit clients.'
  },
  {
    section: 'Nonprofit & Cause',
    stage: 2,
    name: 'Petition / Pledge Signed',
    purpose: 'Capture advocacy leads for cultivation.',
    notes: 'Supporter signs petition or takes pledge; consent for follow-up captured.',
    signal: 'petition_sign (generate_lead)',
    when: 'Advocacy-led organizations.'
  },
  {
    section: 'Nonprofit & Cause',
    stage: 5,
    name: 'Peer-to-Peer Fundraiser Created',
    purpose: 'Turn supporters into acquisition channels.',
    notes: 'Supporter launches a personal fundraising page; funds raised attributed.',
    signal: 'p2p_fundraiser_create',
    when: 'Event- and community-driven nonprofits.'
  },
  {
    section: 'Offline & Cross-Channel Attribution',
    stage: 1,
    name: 'QR Code Scan (Packaging / DM / OOH)',
    purpose: 'Connect offline media and packaging to digital journeys.',
    notes:
      'Scan resolves to a tracked landing experience; source (mailer, package insert, OOH) encoded.',
    signal: 'qr_scan (utm)',
    when: 'CPG, brands using direct mail/OOH/CTV.'
  },
  {
    section: 'Offline & Cross-Channel Attribution',
    stage: 3,
    name: 'Promo Code Redemption (Offline Media)',
    purpose: 'Attribute conversions to podcast/TV/radio/influencer media.',
    notes: 'Channel-exclusive code redeemed at checkout; code-to-channel mapping maintained.',
    signal: 'purchase (promo_code)',
    when: 'Podcast/audio/CTV-heavy media mixes.'
  },
  {
    section: 'Offline & Cross-Channel Attribution',
    stage: 1,
    name: 'Branded Search Lift Window',
    purpose: 'Measure halo from non-click media.',
    notes: 'Branded query volume monitored against CTV/audio/OOH flight windows.',
    signal: 'Branded Search Volume',
    when: 'Upper-funnel media investments.'
  },
  {
    section: 'Offline & Cross-Channel Attribution',
    stage: 3,
    name: 'Call Center Order Placed',
    purpose: 'Capture phone-assisted conversions in high-touch categories.',
    notes:
      'Agent-assisted order tied back to digital session via tracked number or reference code.',
    signal: 'phone_order (matched)',
    when: 'Older demographics, complex products (medical alert, catalogs).'
  },
  {
    section: 'Configurators & High-Consideration Tools',
    stage: 2,
    name: 'Product Configurator Completed',
    purpose: 'Capture deep engagement on customizable products.',
    notes:
      'User completes a build (custom furniture, apparel, PC, bike); configuration saved to profile.',
    signal: 'configurator_complete',
    when: 'Custom/made-to-order products (Custom Ink-type, closets, bikes).'
  },
  {
    section: 'Configurators & High-Consideration Tools',
    stage: 2,
    name: 'Saved Build / Project Retrieved',
    purpose: 'Measure multi-session consideration on big tickets.',
    notes: 'User saves and returns to a configuration or project plan; return-visit depth tracked.',
    signal: 'build_save / retrieve',
    when: 'Home projects, custom goods.'
  },
  {
    section: 'Configurators & High-Consideration Tools',
    stage: 2,
    name: 'Virtual / AR Try-On or Room Placement',
    purpose: 'Reduce uncertainty on fit and aesthetics.',
    notes:
      'User places product in room via AR or tries on virtually; usage correlated with conversion and returns.',
    signal: 'ar_session',
    when: 'Furniture, eyewear, beauty, footwear.'
  },
  {
    section: 'Configurators & High-Consideration Tools',
    stage: 2,
    name: 'Design Proposal / Rendering Delivered',
    purpose: 'Track the assisted-selling artifact in consultative retail.',
    notes: 'Designer delivers rendering/proposal after consultation; proposal views tracked.',
    signal: 'proposal_view',
    when: 'Closets, kitchens, landscaping, custom furniture.'
  }
];
